/**
 * Módulo cliente HTTP — request, authenticatedRequest y authenticatedFormDataRequest con manejo de errores y redirección
 */

import API_URL from '../../config/api';
import navigationHelper from '../../utils/navigationHelper';
import { parseError } from '../../utils/errorMessages';

/**
 * Detecta errores de red cross-browser
 *
 * @param {Error} error
 * @returns {boolean}
 */
function isNetworkError(error) {
  return error instanceof TypeError && (
    error.message.includes('fetch') ||
    error.message.includes('Failed') ||
    error.message.includes('network')
  );
}

/**
 * Crea el cliente HTTP para comunicación con el backend
 *
 * Contrato de retorno dual de authenticatedFormDataRequest:
 * - Si options.responseType === 'blob' → retorna la Response cruda sin consumir
 * - En cualquier otro caso → retorna el objeto JSON parseado
 *
 * @param {Object} deps - Dependencias inyectadas
 * @param {Object} deps.authStorage - Módulo de almacenamiento de autenticación
 * @param {Object} [deps.mutationQueue] - Cola de mutaciones offline (opcional)
 * @returns {Object} Métodos HTTP: baseURL, request, authenticatedRequest, authenticatedFormDataRequest
 */
export function createHttpClient({ authStorage, mutationQueue = null }) {
  const baseURL = API_URL;

  /**
   * Realiza una petición HTTP al backend
   *
   * @param {string} endpoint - Ruta del endpoint (ej: '/api/equipos')
   * @param {Object} [options] - Opciones de fetch
   * @param {string} [options.method] - Método HTTP
   * @param {Object} [options.headers] - Headers adicionales
   * @param {string} [options.body] - Body de la petición
   * @param {string} [options.responseType] - 'blob' para descargas
   * @returns {Promise<Object>}
   * @throws {Error} En caso de error de red o respuesta no ok
   */
  async function request(endpoint, options = {}) {
    const url = `${baseURL}${endpoint}`;

    const defaultHeaders = options.responseType === 'blob'
      ? {}
      : { 'Content-Type': 'application/json' };

    const config = {
      headers: {
        ...defaultHeaders,
        ...options.headers
      },
      ...options
    };

    const responseType = options.responseType;
    delete config.responseType;

    try {
      const response = await fetch(url, config);

      if (responseType === 'blob') {
        if (!response.ok) {
          const errorData = await response.json();

          if (response.status === 401 && !endpoint.includes('/login')) {
            authStorage.removeToken();
            navigationHelper.toLogin();
            throw new Error('Sesion expirada. Por favor, inicia sesion nuevamente.');
          }

          if (response.status === 403) {
            console.warn(`[Permiso faltante] ${errorData.message || 'Acceso denegado'} — endpoint: ${endpoint}`);
            return errorData;
          }

          const errorMessage = errorData.message || `HTTP ${response.status}: Error en la descarga`;
          throw new Error(errorMessage);
        }

        const blob = await response.blob();
        return { blob };
      }

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401 && !endpoint.includes('/login')) {
          authStorage.removeToken();
          navigationHelper.toLogin();
          throw new Error('Sesion expirada. Por favor, inicia sesion nuevamente.');
        }

        if (response.status === 403) {
          console.warn(`[Permiso faltante] ${data.message || 'Acceso denegado'} — endpoint: ${endpoint}`);
          return data;
        }

        if (response.status === 400 || response.status === 401 || response.status === 409 || response.status === 413 || response.status === 429) {
          return data;
        }

        const errorMessage = data.message || `HTTP ${response.status}: Error en la solicitud`;
        throw new Error(errorMessage);
      }

      return data;
    } catch (error) {
      console.error('API Request Error:', error);

      if (isNetworkError(error)) {
        throw new Error('NETWORK_ERROR');
      }

      const parsedError = parseError(error);
      const enhancedError = new Error(parsedError.message);
      enhancedError.code = parsedError.code;
      enhancedError.isRetryable = parsedError.isRetryable;
      enhancedError.originalError = error;

      throw enhancedError;
    }
  }

  /**
   * Realiza una petición autenticada (agrega Bearer token)
   *
   * @param {string} endpoint - Ruta del endpoint
   * @param {Object} [options] - Opciones de fetch
   * @returns {Promise<Object>}
   */
  async function authenticatedRequest(endpoint, options = {}) {
    try {
      const authHeaders = authStorage.getAuthHeaders();
      const config = {
        ...options,
        headers: {
          ...authHeaders,
          ...options.headers
        }
      };

      return await request(endpoint, config);
    } catch (error) {
      // Intercept NETWORK_ERROR for mutations — enqueue offline
      if (
        error.message === 'NETWORK_ERROR' &&
        mutationQueue &&
        !mutationQueue.isReplaying &&
        ['POST', 'PUT', 'DELETE'].includes((options.method || 'GET').toUpperCase())
      ) {
        try {
          await mutationQueue.add({
            method: (options.method || 'GET').toUpperCase(),
            endpoint: `${baseURL}${endpoint}`,
            body: options.body,
            timestamp: Date.now()
          });
          return { success: true, offline: true, message: 'Operación guardada offline — se sincronizará al reconectar' };
        } catch {
          // IndexedDB failed — throw original error
          throw error;
        }
      }

      if (error.message.includes('token')) {
        navigationHelper.toLogin();
      }
      throw error;
    }
  }

  /**
   * Realiza petición autenticada con FormData (para uploads y descargas)
   *
   * Retorno dual según options.responseType:
   * - 'blob' → retorna la Response cruda (sin consumir) para que el caller extraiga blob y filename
   * - (default) → retorna el objeto JSON parseado
   *
   * @param {string} endpoint - Ruta del endpoint
   * @param {Object} [options] - Opciones de fetch
   * @param {FormData} [options.body] - FormData con archivo
   * @param {string} [options.responseType] - 'blob' para descargas
   * @returns {Promise<Object|Response>}
   */
  async function authenticatedFormDataRequest(endpoint, options = {}) {
    const token = authStorage.getToken();
    if (!token || authStorage.isTokenExpired(token)) {
      authStorage.removeToken();
      navigationHelper.toLogin();
      throw new Error('Sesion expirada. Por favor, inicia sesion nuevamente.');
    }

    let response;
    try {
      response = await fetch(`${baseURL}${endpoint}`, {
        ...options,
        headers: {
          'Authorization': `Bearer ${token}`,
          ...options.headers
        }
      });
    } catch (error) {
      if (isNetworkError(error)) {
        throw new Error('NETWORK_ERROR');
      }
      throw error;
    }

    if (options.responseType === 'blob') {
      if (!response.ok) {
        if (response.status === 401) {
          authStorage.removeToken();
          navigationHelper.toLogin();
          throw new Error('Sesion expirada. Por favor, inicia sesion nuevamente.');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}`);
      }
      return response;
    }

    const data = await response.json();
    if (!response.ok) {
      if (response.status === 401) {
        authStorage.removeToken();
        navigationHelper.toLogin();
        throw new Error('Sesion expirada. Por favor, inicia sesion nuevamente.');
      }
      if (response.status === 400 || response.status === 409 || response.status === 413) {
        return data;
      }
      throw new Error(data.message || `Error ${response.status}`);
    }
    return data;
  }

  return { baseURL, request, authenticatedRequest, authenticatedFormDataRequest };
}
